import moment from 'moment'
import { OngoingReportingStatus } from '../../interfaces/bbc'
import { ClientInfoStatus } from '../../interfaces/client'
import { LOAN_TYPES, UCC_SEARCH_LABEL } from '../client'
import { FilterFieldType, IFilter } from './types'

const ACTION_REQUIRED_STATUSES = [
  OngoingReportingStatus.NotStarted,
  OngoingReportingStatus.Uploaded,
  OngoingReportingStatus.Process,
  OngoingReportingStatus.InReview,
]

export const ONGOING_REPORTING_LIST_FILTERS_CONFIG = [
  {
    field: 'type',
    type: FilterFieldType.List,
    isMultiple: true,
    isSortable: true,
    title: 'Analysis Type',
    alias: 'reporting',
  },
  {
    field: 'clientName',
    type: FilterFieldType.Autocomplete,
    alias: 'reporting',
    isSortable: true,
    placeholder: 'Client',
    title: 'Client',
  },
  {
    field: 'firstName',
    type: FilterFieldType.List,
    isSortable: true,
    title: 'Assignee',
    alias: 'assignee',
  },
  {
    field: 'daysSinceDue',
    type: FilterFieldType.Empty,
    title: 'Days Since Due',
    align: 'right',
    isSortable: false,
  },
  {
    field: 'asOfDate',
    type: FilterFieldType.Date,
    alias: 'reporting',
    align: 'left',
    isSortable: true,
    title: 'Reporting Through',
  },
  {
    field: 'createdAt',
    type: FilterFieldType.Date,
    alias: 'reporting',
    align: 'left',
    isSortable: true,
    title: 'Submitted Date',
    isExternalField: true,
  },
  {
    field: 'status',
    type: FilterFieldType.List,
    isMultiple: true,
    isSortable: true,
    title: 'Status',
    alias: 'reporting',
  },
  {
    field: 'action',
    type: FilterFieldType.Empty,
    title: 'Action',
    align: 'right',
  },
  {
    field: 'clientStatus',
    title: 'Client Status',
    dbField: 'client_status',
    type: FilterFieldType.List,
    isMultiple: true,
    excludeColumn: true,
    alias: 'clientInfo',
    options: [
      {
        value: ClientInfoStatus.Current,
        label: ClientInfoStatus.Current,
      },
      {
        value: ClientInfoStatus.Past,
        label: ClientInfoStatus.Past,
      },
      {
        value: ClientInfoStatus.PastArchived,
        label: ClientInfoStatus.PastArchived,
      },
      {
        value: ClientInfoStatus.DueDiligence,
        label: ClientInfoStatus.DueDiligence,
      },
    ],
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        title: 'Action Required',
        filters: {
          status: ACTION_REQUIRED_STATUSES,
        },
      },
      {
        title: 'Submitted This Week',
        filters: {
          createdAtFrom: moment().subtract(7, 'days').format('YYYY-MM-DD'),
        },
      },
      {
        title: 'Archived Reports',
        filters: {
          status: [OngoingReportingStatus.Archived],
        },
      },
      {
        title: 'Assigned To Me',
        filters: {
          firstName: '',
          status: ACTION_REQUIRED_STATUSES,
        },
      },
    ],
  },
] as const satisfies IFilter[]

export const ONGOING_REPORTING_MAPPING_LIST_FILTERS_CONFIG = [
  {
    field: 'indicator',
    type: FilterFieldType.Empty,
    title: '',
    align: 'center',
    isSortable: false,
  },
  {
    field: 'income',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Account',
    title: 'Account',
  },
  {
    field: 'rowKind',
    type: FilterFieldType.Empty,
    title: 'Row type',
    align: 'left',
  },
  {
    field: 'mapping',
    type: FilterFieldType.Empty,
    title: 'Mapping',
    align: 'left',
  },
  {
    field: 'prediction',
    type: FilterFieldType.Empty,
    title: 'Prediction',
    align: 'left',
  },

  {
    field: 'amount1',
    type: FilterFieldType.Amount,
    isSortable: false,
    isCustom: true,
  },
  {
    field: 'amount2',
    type: FilterFieldType.Amount,
    isSortable: false,
    isCustom: true,
  },
  {
    field: 'amount3',
    type: FilterFieldType.Amount,
    isSortable: false,
    isCustom: true,
  },
  {
    field: 'amount4',
    type: FilterFieldType.Amount,
    isSortable: false,
    isCustom: true,
  },
  {
    field: 'amount5',
    type: FilterFieldType.Amount,
    isSortable: false,
    isCustom: true,
  },
  {
    field: 'amount6',
    type: FilterFieldType.Amount,
    isSortable: false,
    isCustom: true,
  },
] as const satisfies IFilter[]

export const COMPLIANCE_BY_CLIENT_LIST_FILTERS_EDITABLE_CONFIG = [
  {
    field: 'capTable',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Cap Table',
  },
  {
    field: 'totalOverdueReports',
    type: FilterFieldType.Empty,
    title: '# Overdue',
    align: 'right',
    isSortable: true,
  },
  {
    field: 'action',
    type: FilterFieldType.Empty,
    title: 'Action',
    align: 'right',
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    fields: [
      {
        field: 'isFinancialsOverdue',
        type: FilterFieldType.Text,
      },
      {
        field: 'userId',
        type: FilterFieldType.List,
      },
    ],
    quickFilters: [
      {
        title: 'My Clients',
        isHidden: true,
        filters: {
          userId: '',
        },
      },
      {
        title: 'Codat Connected',
        filters: {
          isErpConnected: [true],
        },
      },
      {
        title: 'Overdue Financials',
        isHidden: true,
        filters: {
          isFinancialsOverdue: 'true',
        },
      },
    ],
  },
] as const satisfies IFilter[]

export const COMPLIANCE_BY_CLIENT_LIST_FILTERS_CONFIG = [
  {
    field: 'clientName',
    dbField: 'clientNameSort',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Client',
    title: 'Client',
  },
  {
    field: 'loanType',
    type: FilterFieldType.List,
    isMultiple: true,
    isSortable: true,
    options: [
      {
        value: 'all',
        label: 'All',
      },
      ...Object.values(LOAN_TYPES).map((value) => ({
        value,
        label: value,
      })),
    ],
    title: 'Deal Type',
  },
  {
    field: 'isErpConnected',
    type: FilterFieldType.Boolean,
    isMultiple: true,
    isSortable: true,
    options: [
      {
        value: 'all',
        label: 'All',
      },
      {
        value: 'true',
        label: 'Yes',
      },
      {
        value: 'false',
        label: 'No',
      },
    ],
    title: 'Sync',
  },
  {
    field: 'clientIncomeStatement',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Financials',
  },
  {
    field: 'projections',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Projections',
  },
  {
    field: 'salesBySKU',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Sales by SKU',
  },
  {
    field: 'arLedger',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'A/R Ledger',
  },
  {
    field: 'bankTransactions',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Bank Transactions',
  },
  {
    field: 'borrowingBase',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'BBC',
  },
] as const satisfies IFilter[]

export const COMPLIANCE_LEGAL_BY_CLIENT_LIST_FILTERS_CONFIG = [
  {
    field: 'clientName',
    dbField: 'clientNameSort',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Client',
    title: 'Client',
  },
  {
    field: 'uccReportDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: UCC_SEARCH_LABEL,
  },
  {
    field: 'gsReportDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'GS',
  },
  {
    field: 'coiExpiration',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'COI Expiration',
  },
  {
    field: 'eopExpiration',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'EOP Expiration',
  },
  {
    field: 'warehouseWaiverPercentage',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Warehouse Waivers',
  },
  {
    field: 'signatureAuthsReportDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Sig Auths',
  },
  {
    field: 'lienFilingsReportDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Lien Filings',
  },
  {
    field: 'lsaReportDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'LSA',
  },
  {
    field: 'ipsaReportDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'IPSA',
  },
  {
    field: 'noticeOfIpsaReportDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Notice of IPSA',
  },
  {
    field: 'boardConsentReportDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Board Consent',
  },
  {
    field: 'subordinationAgreementsReportDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Subordination Agreements',
  },
  {
    field: 'supportGuaranteeReportDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Support Guarantees',
  },
  {
    field: 'corporateGuaranteesAndPledgesReportDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Corporate Guarantees and Pledges',
  },
] as const satisfies IFilter[]

export const COMPLIANCE_LEGAL_BY_CLIENT_LIST_FILTERS_EDITABLE_CONFIG = [
  {
    field: 'action',
    type: FilterFieldType.Empty,
    title: 'Action',
    align: 'right',
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    fields: [
      {
        field: 'isFinancialsOverdue',
        type: FilterFieldType.Text,
      },
      {
        field: 'userId',
        type: FilterFieldType.List,
      },
    ],
    quickFilters: [
      {
        title: 'My Clients',
        isHidden: true,
        filters: {
          userId: '',
        },
      },
    ],
  },
] as const satisfies IFilter[]

export const LIST_INVENTORY_MAPPING_FILTERS_CONFIG = [
  {
    field: 'sku',
    type: FilterFieldType.Text,
    isSortable: true,
    align: 'left',
    title: 'SKU',
  },
  {
    field: 'description',
    type: FilterFieldType.Text,
    isSortable: true,
    align: 'left',
    title: 'Description',
  },
  {
    field: 'type',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Type',
    align: 'left',
  },
  {
    field: 'brandCategory',
    dbField: 'brand_category',
    type: FilterFieldType.Text,
    isSortable: true,
    align: 'left',
    title: 'Category',
  },
  {
    field: 'industrySubcategory',
    dbField: 'industry_subcategory',
    type: FilterFieldType.Text,
    isSortable: true,
    align: 'left',
    title: 'Sub category',
  },
  {
    field: 'season',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Season',
    align: 'left',
  },
  {
    field: 'size',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Size',
    align: 'left',
  },
  {
    field: 'flavor',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Flavor',
    align: 'left',
  },
  {
    field: 'style',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Style',
    align: 'left',
  },
  {
    field: 'color',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Color',
    align: 'left',
  },
] as const satisfies IFilter[]
